import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, Loader2, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success(`Bienvenue, ${data.user.name}`);
      navigate(data.user.role === "admin" ? "/admin" : "/driver");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Visual side */}
      <div className="relative hidden lg:flex lg:w-1/2 items-end p-12 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1592805144716-feeccccef5ac?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Camion au coucher du soleil"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/60 to-transparent" />
        <div className="relative z-10 fade-in">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-[#F59E0B] flex items-center justify-center">
              <Truck className="w-6 h-6 text-[#0B0F17]" />
            </div>
            <span className="font-display font-extrabold text-2xl tracking-tight">Punch Transprestige</span>
          </div>
          <h1 className="font-display text-4xl font-extrabold leading-tight max-w-md">
            La gestion du temps de vos chauffeurs, simplifiée.
          </h1>
          <p className="text-slate-300 mt-4 max-w-md">
            Punch in / punch out mobile, suivi des heures jour par jour et rapports PDF pour l'administration.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-in">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl bg-[#F59E0B] flex items-center justify-center">
              <Truck className="w-5 h-5 text-[#0B0F17]" />
            </div>
            <span className="font-display font-extrabold text-xl">Punch Transprestige</span>
          </div>

          <h2 className="font-display text-2xl font-bold mb-1">Connexion</h2>
          <p className="text-sm text-slate-400 mb-6">Accédez à votre feuille de temps</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  required
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@exemple.ca"
                  className="pl-9 bg-[#131C2E] border-[#1E293B] h-11"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Mot de passe</Label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  required
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 bg-[#131C2E] border-[#1E293B] h-11"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-[#F59E0B] hover:bg-[#D97706] text-[#0B0F17] font-semibold text-base transition-colors"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Se connecter"}
            </Button>
          </form>

          <p className="text-xs text-slate-500 mt-6 text-center">
            Les comptes chauffeurs sont créés par l'administrateur.
          </p>
        </div>
      </div>
    </div>
  );
}
